/**
 * useCopyToClipboard — Copy text with a transient "copied" indicator.
 *
 * Returns a `copy` callback that writes the given text via the
 * Clipboard API and flips the `copied` flag to `true` for
 * `resetAfterMs` milliseconds (default 2 s). The flag automatically
 * resets so callers can show ephemeral confirmation labels without
 * managing their own timer.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import { useCallback, useEffect, useRef, useState } from "react";

export interface UseCopyToClipboardOptions {
  /** How long the `copied` flag stays true. Defaults to 2000 ms. */
  readonly resetAfterMs?: number;
}

export interface UseCopyToClipboardResult {
  /** True for `resetAfterMs` after the most recent successful copy. */
  readonly copied: boolean;
  /** Error message from the most recent failed copy, else `null`. */
  readonly error: string | null;
  /** Returns `true` on success, `false` on failure. */
  readonly copy: (text: string) => Promise<boolean>;
}

export function useCopyToClipboard(
  options: UseCopyToClipboardOptions = {},
): UseCopyToClipboardResult {
  const { resetAfterMs = 2000 } = options;
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => (): void => {
      if (timerRef.current !== null) clearTimeout(timerRef.current);
    },
    [],
  );

  const copy = useCallback(
    async (text: string): Promise<boolean> => {
      try {
        const clipboard =
          typeof navigator === "undefined"
            ? undefined
            : (navigator as { clipboard?: { writeText?: unknown } }).clipboard;
        const writeText = clipboard?.writeText;
        if (typeof writeText !== "function") {
          throw new Error("Clipboard API is not available");
        }
        await navigator.clipboard.writeText(text);
        setCopied(true);
        setError(null);
        if (timerRef.current !== null) clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => {
          setCopied(false);
        }, resetAfterMs);
        return true;
      } catch (e) {
        setCopied(false);
        setError(e instanceof Error ? e.message : String(e));
        return false;
      }
    },
    [resetAfterMs],
  );

  return { copied, error, copy };
}
